import React, { useState, useMemo } from "react";
import { PlusIcon, CheckIcon, XIcon } from "../icons";
import { fmtDate } from "../utils/helpers";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// ─── Icons ────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const EditPenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────
function getCustomerOrders(customerName, jobs, progressJobs) {
  const completed = jobs
    .filter((j) => j.customer === customerName)
    .map((j) => ({ ...j, status: "completed" }));
  const inProgress = progressJobs
    .filter((j) => j.customerName === customerName)
    .map((j) => ({
      id: j.id,
      date: j.dateOpened,
      customer: j.customerName,
      customerCost: j.customerCost,
      ourCost: j.ourCost,
      profit: j.profit,
      jobNumber: j.jobNumber,
      status: "in_progress",
    }));
  return [...completed, ...inProgress].sort((a, b) => b.date.localeCompare(a.date));
}

function getCustomerStats(customerName, jobs, progressJobs) {
  const orders = getCustomerOrders(customerName, jobs, progressJobs);
  const totalSpent = orders.reduce((s, o) => s + o.customerCost, 0);
  const totalProfit = orders.reduce((s, o) => s + o.profit, 0);
  const totalCost = orders.reduce((s, o) => s + o.ourCost, 0);
  const avgMargin = totalSpent > 0 ? (totalProfit / totalSpent) * 100 : 0;
  const completedCount = orders.filter((o) => o.status === "completed").length;
  const activeCount = orders.filter((o) => o.status === "in_progress").length;
  return { totalSpent, totalProfit, totalCost, avgMargin, completedCount, activeCount, orderCount: orders.length };
}

// ─── Add Customer Modal ───────────────────────────────
function AddCustomerForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ name: "", contactName: "", email: "", phone: "", company: "", address: "", notes: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const canSave = form.name.trim() && (form.email.trim() || form.phone.trim());

  return (
    <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 24, marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F" }}>New Customer</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
        <div>
          <label style={S.label}>Name *</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Customer name" style={S.input} />
        </div>
        <div>
          <label style={S.label}>Company</label>
          <input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company name" style={S.input} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={S.label}>Contact Name</label>
          <input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Primary contact person" style={S.input} />
        </div>
        <div>
          <label style={S.label}>Email *</label>
          <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@example.com" style={S.input} />
        </div>
        <div>
          <label style={S.label}>Phone *</label>
          <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(212) 555-0000" style={S.input} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={S.label}>Address</label>
          <input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street address, city, state, zip" style={S.input} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={S.label}>Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Any notes about this customer..."
            rows={2}
            style={{ ...S.input, resize: "vertical", fontFamily: "'Archivo', sans-serif" }}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ ...S.clearBtn, fontSize: 14 }}>Cancel</button>
        <button
          onClick={() => canSave && onSave(form)}
          style={{ ...S.saveJobBtn, width: "auto", opacity: canSave ? 1 : 0.4, cursor: canSave ? "pointer" : "default" }}
        >
          Add Customer
        </button>
      </div>
      <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 8 }}>* Name and at least one of email or phone required</p>
    </div>
  );
}

// ─── Customer Detail View ─────────────────────────────
function CustomerDetail({ customer, customers, setCustomers, jobs, progressJobs, onBack }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...customer });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const stats = useMemo(() => getCustomerStats(customer.name, jobs, progressJobs), [customer.name, jobs, progressJobs]);
  const orders = useMemo(() => getCustomerOrders(customer.name, jobs, progressJobs), [customer.name, jobs, progressJobs]);

  const saveEdit = () => {
    if (!form.name.trim()) return;
    fetch(`${API_URL}/api/customers/${customer.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((r) => r.json())
      .then((updated) => setCustomers(customers.map((c) => (c.id === customer.id ? updated : c))))
      .catch(() => setCustomers(customers.map((c) => (c.id === customer.id ? { ...form } : c))));
    setEditing(false);
  };

  const cancelEdit = () => {
    setForm({ ...customer });
    setEditing(false);
  };

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "rgba(10,10,15,0.58)", fontSize: 15, cursor: "pointer", fontFamily: "'Archivo', sans-serif", marginBottom: 20, padding: 0 }}
      >
        <BackIcon /> All Customers
      </button>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>{customer.name}</h2>
          <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>
            Customer since {customer.dateAdded ? new Date(customer.dateAdded + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}
          </p>
        </div>
        {!editing && (
          <button onClick={() => { setForm({ ...customer }); setEditing(true); }} style={S.addBtn}>
            <EditPenIcon /> Edit Info
          </button>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}>
          <span style={S.statLabel}>Total Spent</span>
          <span style={{ ...S.statValue, color: "#0033FF" }}>${stats.totalSpent.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Profit Generated</span>
          <span style={{ ...S.statValue, color: "#34d399" }}>${stats.totalProfit.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Our Costs</span>
          <span style={S.statValue}>${stats.totalCost.toLocaleString()}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Avg Margin</span>
          <span style={S.statValue}>{stats.avgMargin.toFixed(1)}%</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Total Orders</span>
          <span style={S.statValue}>{stats.orderCount}</span>
        </div>
      </div>

      {/* Info card */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Customer Information</span>
          {editing && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={saveEdit} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e" }}><CheckIcon /></button>
              <button onClick={cancelEdit} style={{ ...S.iconBtn, borderColor: "#ef4444", color: "#ef4444" }}><XIcon /></button>
            </div>
          )}
        </div>
        <div style={{ padding: 20 }}>
          {editing ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div>
                <label style={S.label}>Name</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Company</label>
                <input value={form.company} onChange={(e) => set("company", e.target.value)} style={S.input} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={S.label}>Contact Name</label>
                <input value={form.contactName || ""} onChange={(e) => set("contactName", e.target.value)} placeholder="Primary contact person" style={S.input} />
              </div>
              <div>
                <label style={S.label}>Email</label>
                <input value={form.email} onChange={(e) => set("email", e.target.value)} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Phone</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} style={S.input} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={S.label}>Address</label>
                <input value={form.address} onChange={(e) => set("address", e.target.value)} style={S.input} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={S.label}>Notes</label>
                <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} style={{ ...S.input, resize: "vertical", fontFamily: "'Archivo', sans-serif" }} />
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 32px" }}>
              <InfoRow label="Company" value={customer.company} />
              <InfoRow label="Contact Name" value={customer.contactName} />
              <InfoRow label="Email" value={customer.email} />
              <InfoRow label="Phone" value={customer.phone} />
              <InfoRow label="Address" value={customer.address} />
              {customer.notes && <InfoRow label="Notes" value={customer.notes} span />}
            </div>
          )}
        </div>
      </div>

      {/* Order History */}
      <div style={{ marginBottom: 4 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 12 }}>
          Order History
          <span style={{ ...S.jobCountBadge, marginLeft: 10 }}>{orders.length}</span>
        </h3>
      </div>
      {orders.length > 0 ? (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: "14%" }}>Date</th>
                <th style={{ ...S.th, width: "14%" }}>Job #</th>
                <th style={{ ...S.th, width: "14%" }}>Status</th>
                <th style={{ ...S.th, width: "18%", textAlign: "right" }}>Customer Cost</th>
                <th style={{ ...S.th, width: "18%", textAlign: "right" }}>Our Cost</th>
                <th style={{ ...S.th, width: "22%", textAlign: "right" }}>Profit</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const mg = o.customerCost > 0 ? (o.profit / o.customerCost) * 100 : 0;
                return (
                  <tr key={o.id}>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "rgba(10,10,15,0.58)" }}>
                      {fmtDate(o.date)}
                    </td>
                    <td style={S.td}>
                      {o.jobNumber ? (
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0033FF", fontWeight: 600 }}>{o.jobNumber}</span>
                      ) : (
                        <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 14 }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        display: "inline-block",
                        padding: "2px 10px",
                        borderRadius: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        background: o.status === "completed" ? "#0c3a2a" : "#F4F2EC",
                        color: o.status === "completed" ? "#34d399" : "#0033FF",
                        border: `1px solid ${o.status === "completed" ? "#34d39930" : "rgba(0,51,255,0.18)"}`,
                      }}>
                        {o.status === "completed" ? "Completed" : "In Progress"}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>
                      ${o.customerCost.toLocaleString()}
                    </td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>
                      ${o.ourCost.toLocaleString()}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                        <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "2px 8px", borderRadius: 0}}>{mg.toFixed(0)}%</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#34d399" }}>${o.profit.toLocaleString()}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
          No orders yet for this customer.
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, span }) {
  return (
    <div style={span ? { gridColumn: "1 / -1" } : {}}>
      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, color: value ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{value || "—"}</div>
    </div>
  );
}

// ─── Customer List View ───────────────────────────────
function CustomerList({ customers, jobs, progressJobs, onSelect, onAdd }) {
  const [filter, setFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showAddForm, setShowAddForm] = useState(false);

  // Enrich customers with stats
  const enriched = useMemo(() => {
    return customers.map((c) => ({
      ...c,
      stats: getCustomerStats(c.name, jobs, progressJobs),
    }));
  }, [customers, jobs, progressJobs]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter) {
      const f = filter.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(f) ||
          c.company.toLowerCase().includes(f) ||
          c.email.toLowerCase().includes(f) ||
          c.phone.includes(f)
      );
    }
    if (sortCol) {
      list = [...list].sort((a, b) => {
        let va, vb;
        if (sortCol === "totalSpent") { va = a.stats.totalSpent; vb = b.stats.totalSpent; }
        else if (sortCol === "totalProfit") { va = a.stats.totalProfit; vb = b.stats.totalProfit; }
        else if (sortCol === "orderCount") { va = a.stats.orderCount; vb = b.stats.orderCount; }
        else { va = String(a[sortCol] || "").toLowerCase(); vb = String(b[sortCol] || "").toLowerCase(); }
        if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return list;
  }, [enriched, filter, sortCol, sortDir]);

  const handleSort = (c) => {
    if (sortCol === c) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  // Totals
  const totalSpentAll = enriched.reduce((s, c) => s + c.stats.totalSpent, 0);
  const totalProfitAll = enriched.reduce((s, c) => s + c.stats.totalProfit, 0);
  const activeCustomers = enriched.filter((c) => c.stats.activeCount > 0).length;

  const SortArrow = ({ col }) => {
    if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>&#8597;</span>;
    return <span style={{ marginLeft: 4, color: "#0033FF" }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Customers</span><span style={S.statValue}>{customers.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Active Now</span><span style={{ ...S.statValue, color: "#0033FF" }}>{activeCustomers}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Lifetime Revenue</span><span style={S.statValue}>${totalSpentAll.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Lifetime Profit</span><span style={{ ...S.statValue, color: "#34d399" }}>${totalProfitAll.toLocaleString()}</span></div>
      </div>

      {/* Filter + Add */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search customers..."
          style={{ ...S.input, marginBottom: 0, maxWidth: 280 }}
        />
        <button onClick={() => setShowAddForm(true)} style={S.addBtn}><PlusIcon /> Add Customer</button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <AddCustomerForm
          onSave={(form) => { onAdd(form); setShowAddForm(false); }}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th onClick={() => handleSort("name")} style={{ ...S.th, width: "24%", cursor: "pointer", userSelect: "none" }}>Customer<SortArrow col="name" /></th>
              <th style={{ ...S.th, width: "18%" }}>Contact</th>
              <th onClick={() => handleSort("orderCount")} style={{ ...S.th, width: "10%", textAlign: "center", cursor: "pointer", userSelect: "none" }}>Orders<SortArrow col="orderCount" /></th>
              <th onClick={() => handleSort("totalSpent")} style={{ ...S.th, width: "16%", textAlign: "right", cursor: "pointer", userSelect: "none" }}>Total Spent<SortArrow col="totalSpent" /></th>
              <th onClick={() => handleSort("totalProfit")} style={{ ...S.th, width: "16%", textAlign: "right", cursor: "pointer", userSelect: "none" }}>Profit<SortArrow col="totalProfit" /></th>
              <th style={{ ...S.th, width: "16%", textAlign: "right" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr
                key={c.id}
                onClick={() => onSelect(c)}
                style={{ cursor: "pointer", transition: "background .1s" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22,45,80,0.4)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={S.td}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#0A0A0F", marginBottom: 2 }}>{c.name}</div>
                    {c.contactName && (
                      <div style={{ fontSize: 13, color: "#0A0A0F" }}>{c.contactName}</div>
                    )}
                  </div>
                </td>
                <td style={S.td}>
                  <div>
                    <div style={{ fontSize: 14, color: "#0A0A0F" }}>{c.email || "—"}</div>
                    <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{c.phone || ""}</div>
                  </div>
                </td>
                <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "#0A0A0F" }}>
                  {c.stats.orderCount}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: c.stats.totalSpent > 0 ? "#0033FF" : "rgba(10,10,15,0.58)" }}>
                  {c.stats.totalSpent > 0 ? `$${c.stats.totalSpent.toLocaleString()}` : "—"}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: c.stats.totalProfit > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                  {c.stats.totalProfit > 0 ? `$${c.stats.totalProfit.toLocaleString()}` : "—"}
                </td>
                <td style={{ ...S.td, textAlign: "right" }}>
                  {c.stats.activeCount > 0 ? (
                    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 0, fontSize: 13, fontWeight: 600, background: "#F4F2EC", color: "#0033FF", border: "1px solid #0033FF30" }}>
                      {c.stats.activeCount} active
                    </span>
                  ) : c.stats.completedCount > 0 ? (
                    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 0, fontSize: 13, fontWeight: 600, background: "#0c3a2a", color: "#34d399", border: "1px solid #34d39930" }}>
                      Returning
                    </span>
                  ) : (
                    <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>New</span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>
                  {filter ? "No customers match your search." : "No customers yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────
export default function CustomersPage({ customers, setCustomers, jobs, progressJobs }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleAdd = (form) => {
    const newCustomer = {
      name: form.name.trim(),
      contactName: form.contactName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim() || form.name.trim(),
      address: form.address.trim(),
      notes: form.notes.trim(),
      dateAdded: new Date().toISOString().slice(0, 10),
    };
    fetch(`${API_URL}/api/customers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newCustomer),
    })
      .then((r) => r.json())
      .then((created) => setCustomers([...customers, created]))
      .catch(() => setCustomers([...customers, { ...newCustomer, id: Date.now() }]));
  };

  // Keep selected customer in sync after edits
  const current = selectedCustomer ? customers.find((c) => c.id === selectedCustomer.id) : null;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Customers</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Manage customer directory and track order history</p>
      </div>
      {current ? (
        <CustomerDetail
          customer={current}
          customers={customers}
          setCustomers={setCustomers}
          jobs={jobs}
          progressJobs={progressJobs}
          onBack={() => setSelectedCustomer(null)}
        />
      ) : (
        <CustomerList
          customers={customers}
          jobs={jobs}
          progressJobs={progressJobs}
          onSelect={setSelectedCustomer}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
}
